import { useEffect, useRef, useState } from 'react';

import { AvatarSize } from '../../../../../../../UserAliasAvatar/constants';
import { UserAliasAvatar } from '../../../../../../../UserAliasAvatar/UserAliasAvatar';
import { PositionChange } from '../../../../../../hooks/useCalculateRankingPositionChanges';
import { CHANGE_POSITION_TIMEOUT } from '../../constants';
import type { MedalPosition } from '../../types/MedalPosition';
import { Transition } from './types/Transition';
import { StyledMedalIcon } from '../Common.styled';
import {
  PositionWrapper,
  StyledBasicParagraph,
  StyledDisplayDownIcon,
  StyledDisplayUpIcon,
  UserAlias,
  UserAliasAvatarWrapper,
  UserAliasWrapper,
  Wrapper,
} from './PodiumUser.styled';

interface PodiumUserProps {
  userAlias?: string;
  podiumSize: AvatarSize;
  isActive?: boolean;
  podiumIndex: number;
  medalPosition: MedalPosition;
  hasEnterAnimation?: boolean;
  enterDelayAnimation?: number;
  positionChange: PositionChange;
  onEnterAnimationEnd?: () => void;
  onChangePositionTimeout: () => void;
}

export const PodiumUser = ({
  userAlias,
  podiumSize,
  isActive,
  podiumIndex,
  medalPosition,
  hasEnterAnimation,
  enterDelayAnimation,
  positionChange,
  onEnterAnimationEnd,
  onChangePositionTimeout,
}: PodiumUserProps) => {
  const [currentProps, setCurrentProps] = useState<PodiumUserProps>({
    podiumSize,
    podiumIndex,
    medalPosition,
    positionChange,
    onChangePositionTimeout,
  });

  const [nextProps, setNextProps] = useState<
    Pick<PodiumUserProps, 'userAlias' | 'isActive'> | undefined
  >();
  const [transition, setTransition] = useState<Transition | undefined>();
  const [showUpArrow, setShowUpArrow] = useState(false);
  const [showDownArrow, setShowDownArrow] = useState(false);
  const isFirstRender = useRef(true);

  const [displayChangePositionTimeout, setDisplayChangePositionTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [internalPositionChange, setInternalPositionChange] = useState<PositionChange>(
    PositionChange.SAME,
  );

  useEffect(() => {
    if (positionChange !== PositionChange.SAME) {
      setInternalPositionChange(positionChange);
      setDisplayChangePositionTimeout(
        setTimeout(() => {
          setInternalPositionChange(PositionChange.SAME);
          setDisplayChangePositionTimeout(null);
          onChangePositionTimeout();
        }, CHANGE_POSITION_TIMEOUT),
      );
    }

    return () => {
      if (displayChangePositionTimeout) {
        clearTimeout(displayChangePositionTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChangePositionTimeout, positionChange]);

  useEffect(() => {
    if (hasEnterAnimation) {
      setCurrentProps(prev => ({ ...prev, userAlias, isActive }));
      setTransition(undefined);
    } else if (!isFirstRender.current && userAlias !== currentProps?.userAlias) {
      setNextProps({ userAlias, isActive });
      setTransition(Transition.Exit);
    } else if (isFirstRender.current) {
      setCurrentProps(prev => ({ ...prev, userAlias, isActive }));
      isFirstRender.current = false;
    }
  }, [
    userAlias,
    podiumSize,
    isActive,
    podiumIndex,
    medalPosition,
    currentProps?.userAlias,
    hasEnterAnimation,
  ]);

  const handleTransitionEnd = () => {
    if (transition === Transition.Exit) {
      setCurrentProps(prev => {
        if (nextProps) {
          return { ...prev, ...nextProps };
        }

        return prev;
      });
      setNextProps(undefined);
      setTransition(Transition.Enter);
    } else {
      setTransition(undefined);
    }
  };

  useEffect(() => {
    if (positionChange === PositionChange.UP) {
      setShowUpArrow(true);
    } else if (positionChange === PositionChange.DOWN) {
      setShowDownArrow(true);
    }
  }, [positionChange]);

  const handleHideUpArrow = () => {
    setShowUpArrow(false);
  };

  const handleHideDownArrow = () => {
    setShowDownArrow(false);
  };

  return (
    <Wrapper>
      <UserAliasAvatarWrapper
        $activeAnimation={hasEnterAnimation}
        $delayAnimation={enterDelayAnimation}
        $transition={transition}
        onTransitionEnd={handleTransitionEnd}
        onAnimationEnd={onEnterAnimationEnd}
      >
        <UserAliasAvatar
          name={currentProps?.userAlias}
          size={currentProps.podiumSize}
          isActive={currentProps?.isActive}
        />
      </UserAliasAvatarWrapper>

      <UserAliasWrapper
        $activeAnimation={hasEnterAnimation}
        $delayAnimation={enterDelayAnimation}
        $transition={transition}
      >
        <UserAlias $hasSpaces={currentProps?.userAlias?.includes(' ')} $centered>
          {currentProps?.userAlias}
        </UserAlias>
      </UserAliasWrapper>

      <PositionWrapper>
        <StyledBasicParagraph>{currentProps.podiumIndex}</StyledBasicParagraph>
        <StyledMedalIcon $position={currentProps.medalPosition} />

        {showUpArrow && (
          <StyledDisplayUpIcon
            $show={internalPositionChange === PositionChange.UP}
            onTransitionEnd={handleHideUpArrow}
          />
        )}

        {showDownArrow && (
          <StyledDisplayDownIcon
            $show={internalPositionChange === PositionChange.DOWN}
            onTransitionEnd={handleHideDownArrow}
          />
        )}
      </PositionWrapper>
    </Wrapper>
  );
};

PodiumUser.PodiumSize = AvatarSize;
